/**
 * listingsSlice.js
 *
 * This Redux slice manages the state related to the vendor's recipe listings.
 *
 * Key Responsibilities:
 * - Create new recipe listings (POST to /vendor/recipes)
 * - Save listings as draft (POST to /vendor/recipes/draft)
 * - Handle global state for loading, errors, and success flags
 * - Append new or draft listings to the state array
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api"; // Reusable Axios instance for backend requests

// -----------------------------
// Async thunk to create a new listing
// -----------------------------
export const createListing = createAsyncThunk(
  "listings/createListing",
  async (listingData, { rejectWithValue }) => {
    try {
      // POST request to create a listing
      const response = await api.post("/vendor/recipes", listingData);
      return response.data.recipe; // Return the newly created recipe
    } catch (err) {
      // Handle any error and return a meaningful message to be used in rejected state
      return rejectWithValue(
        err.response?.data?.error || "Failed to create listing"
      );
    }
  }
);

// -----------------------------
// Async thunk to save a listing as draft
// -----------------------------
export const saveDraft = createAsyncThunk(
  "listings/saveDraft",
  async (draftData, { rejectWithValue }) => {
    try {
      const response = await api.post("/vendor/recipes/draft", draftData);
      return response.data.recipe;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || "Failed to save draft"
      );
    }
  }
);

// -----------------------------
// Slice definition
// -----------------------------
const listingsSlice = createSlice({
  name: "listings", // Slice name used in Redux state
  initialState: {
    items: [],         // All listings (published or draft)
    loading: false,    // Loading state while submitting or saving
    error: null,       // Stores any error message
    success: false,    // Indicates if a listing was created successfully
  },
  reducers: {
    // Reset listing status after navigation or form completion
    resetListingStatus: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // -----------------------------
      // createListing lifecycle
      // -----------------------------
      .addCase(createListing.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createListing.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.items.push(action.payload); // Add the new recipe to the list
      })
      .addCase(createListing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload; // Set error from rejected action
        state.success = false;
      })

      // -----------------------------
      // saveDraft lifecycle
      // -----------------------------
      .addCase(saveDraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveDraft.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push({ ...action.payload, isDraft: true }); // Tag it as a draft
      })
      .addCase(saveDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload; // Set error from rejected action
      });
  },
});

// Export actions for use in components
export const { resetListingStatus } = listingsSlice.actions;

// Export reducer to be added in the Redux store
export default listingsSlice.reducer;
